import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useForm } from "react-hook-form";
import { useGetCategoriesProductsQuery } from "../../apis/CategoriesApi";
import { useUpdateShopProfilesMutation } from "../../apis/ShopProfileApi";
import { CircularProgress } from "@mui/material";

const EditShopForm = ({ profileInfo }) => {
  const [selectedCategories, setSelectedCategories] = useState();
  const [updateShopProfile, { isLoading: isUpdating }] = useUpdateShopProfilesMutation(); // 🟢 Renamed to isUpdating

  const { data: categoriesData, isLoading: isCategoriesLoading, isError } = useGetCategoriesProductsQuery(); // 🟢 Renamed to isCategoriesLoading
  const options = categoriesData
    ? categoriesData.map((category) => ({
        value: category.id,
        label: category.name,
      }))
    : [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      shopDescription: profileInfo?.shopDescription || "",
      streetAddressShopAddress: profileInfo?.streetAddressShopAddress || "",
      cityShopAddress: profileInfo?.cityShopAddress || "",
      stateShopAddress: profileInfo?.stateShopAddress || "",
      zipShopAddress: profileInfo?.zipShopAddress || "",
    },
  });

  const handleChange = (selectedOptions) => {
    setSelectedCategories(selectedOptions || []);
    setValue(
      "categoryProducts",
      selectedOptions.map((option) => ({ id: option.value, name: option.label }))
    ); // Transform options into the required structure
  };

  // Populate selected categories and set them using setValue for the form
  useEffect(() => {
    if (profileInfo?.categoryProducts) {
      const initialCategories = profileInfo.categoryProducts.map((category) => ({
        value: category.id,
        label: category.name,
      }));
      setSelectedCategories(initialCategories);
      setValue("categoryProducts", initialCategories); // Use setValue to set categories field in form
    }
  }, [profileInfo, setValue]);

  const onFormSubmit = async (data) => {
    // Transform the categoryProducts array
    const transformedData = {
      ...data,
      categoryProducts: data.categoryProducts.map((cp) => ({
        id: cp.value, // Map "value" to "id"
        name: cp.label, // Map "label" to "name"
      })),
    };

    try {
      await updateShopProfile({ id: profileInfo?.id, updateDto: transformedData }).unwrap();
      alert("Shop profile updated successfully!");
    } catch (error) {
      console.error("Failed to update shop profile:", error);
      alert("Failed to update shop profile.");
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <div className="row gy-4">
        {/* Shop Category */}
        <div className="col-sm-6">
          <h6>Select Categories</h6>
          <div className="p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
            {isCategoriesLoading && <p>Loading categories...</p>}
            {isError && <p>Error loading categories.</p>}
            {!isCategoriesLoading && !isError && (
              <Select
                className="text-sm font-heading-two text-gray-900 fw-semibold mb-4"
                options={options}
                isMulti
                value={selectedCategories}
                onChange={handleChange}
                placeholder="Select categories..."
                styles={{
                  menu: (provided) => ({ ...provided, zIndex: 9999 }), // Prevent dropdown clipping
                }}
              />
            )}
          </div>
        </div>

        {/* Shop Description */}
        <div className="col-sm-12">
          <label htmlFor="shopDescription">Shop Description</label>
          <textarea
            className="common-input px-16"
            id="shopDescription"
            placeholder="Describe your shop"
            {...register("shopDescription", { required: "Shop description is required" })}
          />
          {errors.shopDescription && <p className="text-red-500 mt-2">{errors.shopDescription.message}</p>}
        </div>

        {/* Address Fields */}
        <div className="col-sm-12">
          <h6>Shop Address</h6>
          <input
            type="text"
            className="common-input px-16"
            placeholder="Street Address"
            {...register("streetAddressShopAddress", { required: "Street Address is required" })}
          />
          {errors.streetAddressShopAddress && (
            <p className="text-red-500 mt-2">{errors.streetAddressShopAddress.message}</p>
          )}

          <input
            type="text"
            className="common-input px-16"
            placeholder="City"
            {...register("cityShopAddress", { required: "City is required" })}
          />
          {errors.cityShopAddress && <p className="text-red-500 mt-2">{errors.cityShopAddress.message}</p>}

          <input
            type="text"
            className="common-input px-16"
            placeholder="State"
            {...register("stateShopAddress", { required: "State is required" })}
          />
          {errors.stateShopAddress && <p className="text-red-500 mt-2">{errors.stateShopAddress.message}</p>}

          <input
            type="text"
            className="common-input px-16"
            placeholder="ZIP Code"
            {...register("zipShopAddress", { required: "ZIP Code is required" })}
          />
          {errors.zipShopAddress && <p className="text-red-500 mt-2">{errors.zipShopAddress.message}</p>}
        </div>

        {/* Submit Button */}
        <div className="col-sm-12 mt-32">
          <button
            type="submit"
            className="btn btn-main py-18 px-32 rounded-8 flex items-center justify-center"
            disabled={isUpdating} // 🟢 Use isUpdating for the mutation loading state
          >
            {isUpdating ? ( // 🟢 Use isUpdating for the mutation loading state
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default EditShopForm;